// Theme mode type
export type ThemeMode = 'light' | 'dark';

export interface DensityPreset {
	contentMaxWidth: string;
	pageGutter: string;
	pageGutterMobile: string;
	sectionGap: string;
	gridGap: string;
	cardPadding: string;
	cardPaddingMobile: string;
	cardRadius: string;
	navigationInset: string;
}

interface TypographyRole {
	fontSize: string;
	fontWeight: number;
	lineHeight: number;
	letterSpacing: string;
}

// UIの密度はここだけで切り替える。各コンポーネントへ数値を散らさない。
export const densityPresets: Record<'compact' | 'comfortable', DensityPreset> = {
	compact: {
		contentMaxWidth: '1180px',
		pageGutter: '1.5rem',
		pageGutterMobile: '0.75rem',
		sectionGap: '2rem',
		gridGap: '0.875rem',
		cardPadding: '1.125rem',
		cardPaddingMobile: '1rem',
		cardRadius: '1rem',
		navigationInset: '0.625rem',
	},
	comfortable: {
		contentMaxWidth: '1200px',
		pageGutter: '2rem',
		pageGutterMobile: '1rem',
		sectionGap: '4rem',
		gridGap: '2rem',
		cardPadding: '2rem',
		cardPaddingMobile: '1.5rem',
		cardRadius: '1.5rem',
		navigationInset: '1.5rem',
	},
};

export const activeDensityPreset = densityPresets.compact;

// Theme type definitions
export interface Theme {
	mode: ThemeMode;
	density: DensityPreset;
	colors: {
		primary: {
			main: string;
			light: string;
			dark: string;
			gradient: string;
		};
		secondary: {
			main: string;
			light: string;
			dark: string;
			gradient: string;
		};
		accent: {
			teal: string;
			purple: string;
			pink: string;
			orange: string;
		};
		neutral: {
			50: string;
			100: string;
			200: string;
			300: string;
			400: string;
			500: string;
			600: string;
			700: string;
			800: string;
			900: string;
		};
		text: {
			primary: string;
			secondary: string;
			tertiary: string;
			inverse: string;
		};
		background: {
			primary: string;
			secondary: string;
			tertiary: string;
		};
	};
	typography: {
		fontFamily: {
			primary: string;
			secondary: string;
			mono: string;
		};
		fontSize: {
			xs: string;
			sm: string;
			base: string;
			lg: string;
			xl: string;
			'2xl': string;
			'3xl': string;
			'4xl': string;
			'5xl': string;
			'6xl': string;
		};
		fontWeight: {
			light: number;
			normal: number;
			medium: number;
			semibold: number;
			bold: number;
			black: number;
		};
		lineHeight: {
			tight: number;
			normal: number;
			relaxed: number;
			loose: number;
		};
		roles: {
			display: TypographyRole;
			pageHeading: TypographyRole;
			sectionHeading: TypographyRole;
			title: TypographyRole;
			body: TypographyRole;
			bodySmall: TypographyRole;
			label: TypographyRole;
			caption: TypographyRole;
		};
	};
	spacing: {
		0: string;
		1: string;
		2: string;
		3: string;
		4: string;
		5: string;
		6: string;
		8: string;
		10: string;
		12: string;
		16: string;
		20: string;
		24: string;
		32: string;
	};
	borderRadius: {
		none: string;
		sm: string;
		base: string;
		md: string;
		lg: string;
		xl: string;
		'2xl': string;
		full: string;
	};
	shadows: {
		sm: string;
		base: string;
		md: string;
		lg: string;
		xl: string;
		'2xl': string;
		inner: string;
		glow: string;
		glowHover: string;
	};
	transitions: {
		fast: string;
		base: string;
		slow: string;
		slower: string;
		bounce: string;
	};
	breakpoints: {
		xs: string;
		sm: string;
		md: string;
		lg: string;
		xl: string;
		'2xl': string;
	};
	glassmorphism: {
		light: string;
		dark: string;
	};
	animations: {
		fadeIn: string;
		slideIn: string;
		scaleIn: string;
		float: string;
		gradientShift: string;
	};
}

// ライトモードテーマ
export const lightTheme: Theme = {
	mode: 'light',
	density: activeDensityPreset,
	colors: {
		// Primary gradient colors
		primary: {
			main: '#0066cc',
			light: '#0071e3',
			dark: '#0055b3',
			gradient: 'linear-gradient(135deg, #0066cc 0%, #0071e3 100%)',
		},
		secondary: {
			main: '#f093fb',
			light: '#f5b4ff',
			dark: '#d77fe8',
			gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
		},
		accent: {
			teal: '#14b8a6',
			purple: '#a78bfa',
			pink: '#f472b6',
			orange: '#fb923c',
		},
		// Neutral colors
		neutral: {
			50: '#fafafa',
			100: '#f4f4f5',
			200: '#e4e4e7',
			300: '#d4d4d8',
			400: '#a1a1aa',
			500: '#71717a',
			600: '#52525b',
			700: '#3f3f46',
			800: '#27272a',
			900: '#18181b',
		},
		// Semantic colors
		text: {
			primary: '#18181b',
			secondary: '#52525b',
			tertiary: '#a1a1aa',
			inverse: '#ffffff',
		},
		background: {
			primary: '#f5f5f7',
			secondary: '#ffffff',
			tertiary: '#e8e8ed',
		},
	},

	typography: {
		fontFamily: {
			primary: '-apple-system, BlinkMacSystemFont, "SF Pro JP", "SF Pro Text", "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Geist", "Noto Sans JP", Meiryo, sans-serif',
			secondary: '-apple-system, BlinkMacSystemFont, "SF Pro JP", "SF Pro Display", "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Geist", "Noto Sans JP", Meiryo, sans-serif',
			mono: '"Geist Mono", "SF Mono", Consolas, monospace',
		},
		fontSize: {
			xs: '0.75rem',    // 12px
			sm: '0.875rem',   // 14px
			base: '1rem',     // 16px
			lg: '1.125rem',   // 18px
			xl: '1.25rem',    // 20px
			'2xl': '1.5rem',  // 24px
			'3xl': '1.875rem', // 30px
			'4xl': '2.25rem', // 36px
			'5xl': '3rem',    // 48px
			'6xl': '3.75rem', // 60px
		},
		fontWeight: {
			light: 300,
			normal: 400,
			medium: 500,
			semibold: 600,
			bold: 700,
			black: 900,
		},
		lineHeight: {
			tight: 1.25,
			normal: 1.5,
			relaxed: 1.75,
			loose: 2,
		},
		roles: {
			display: { fontSize: 'clamp(2.25rem, 5vw, 3.25rem)', fontWeight: 600, lineHeight: 1.08, letterSpacing: '-0.035em' },
			pageHeading: { fontSize: 'clamp(1.875rem, 4vw, 2.25rem)', fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.025em' },
			sectionHeading: { fontSize: 'clamp(1.5rem, 3vw, 1.75rem)', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.02em' },
			title: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
			body: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.65, letterSpacing: '0' },
			bodySmall: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55, letterSpacing: '0' },
			label: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '0' },
			caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4, letterSpacing: '0' },
		},
	},

	spacing: {
		0: '0',
		1: '0.25rem',  // 4px
		2: '0.5rem',   // 8px
		3: '0.75rem',  // 12px
		4: '1rem',     // 16px
		5: '1.25rem',  // 20px
		6: '1.5rem',   // 24px
		8: '2rem',     // 32px
		10: '2.5rem',  // 40px
		12: '3rem',    // 48px
		16: '4rem',    // 64px
		20: '5rem',    // 80px
		24: '6rem',    // 96px
		32: '8rem',    // 128px
	},

	borderRadius: {
		none: '0',
		sm: '0.25rem',   // 4px
		base: '0.375rem', // 6px
		md: '0.5rem',    // 8px
		lg: '0.75rem',   // 12px
		xl: '1rem',      // 16px
		'2xl': '1.25rem', // 20px
		full: '9999px',
	},

	shadows: {
		sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
		base: '0 1px 2px rgba(0, 0, 0, 0.04)',
		md: '0 2px 6px rgba(0, 0, 0, 0.06)',
		lg: '0 4px 12px rgba(0, 0, 0, 0.08)',
		xl: '0 8px 20px rgba(0, 0, 0, 0.1)',
		'2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
		glow: '0 0 20px rgba(102, 126, 234, 0.4)',
		glowHover: '0 0 30px rgba(102, 126, 234, 0.6)',
	},

	transitions: {
		fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
		base: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
		slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
		slower: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
		bounce: '300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
	},

	breakpoints: {
		xs: '480px',
		sm: '640px',
		md: '768px',
		lg: '1024px',
		xl: '1280px',
		'2xl': '1536px',
	},

	// グラスモーフィズム効果
	glassmorphism: {
		light: `
			background: rgba(255, 255, 255, 0.7);
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
			border: 1px solid rgba(255, 255, 255, 0.3);
		`,
		dark: `
			background: rgba(0, 0, 0, 0.2);
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
			border: 1px solid rgba(255, 255, 255, 0.1);
		`,
	},

	// アニメーションキーフレーム
	animations: {
		fadeIn: `
			@keyframes fadeIn {
				from {
					opacity: 0;
					transform: translateY(20px);
				}
				to {
					opacity: 1;
					transform: translateY(0);
				}
			}
		`,
		slideIn: `
			@keyframes slideIn {
				from {
					opacity: 0;
					transform: translateX(-20px);
				}
				to {
					opacity: 1;
					transform: translateX(0);
				}
			}
		`,
		scaleIn: `
			@keyframes scaleIn {
				from {
					opacity: 0;
					transform: scale(0.9);
				}
				to {
					opacity: 1;
					transform: scale(1);
				}
			}
		`,
		float: `
			@keyframes float {
				0%, 100% {
					transform: translateY(0px);
				}
				50% {
					transform: translateY(-10px);
				}
			}
		`,
		gradientShift: `
			@keyframes gradientShift {
				0% {
					background-position: 0% 50%;
				}
				50% {
					background-position: 100% 50%;
				}
				100% {
					background-position: 0% 50%;
				}
			}
		`,
	},
};

// ダークモードテーマ
export const darkTheme: Theme = {
	mode: 'dark',
	density: activeDensityPreset,
	colors: {
		// Primary gradient colors (adjusted for dark mode)
		primary: {
			main: '#2997ff',
			light: '#64b5ff',
			dark: '#0077d9',
			gradient: 'linear-gradient(135deg, #2997ff 0%, #64b5ff 100%)',
		},
		secondary: {
			main: '#f5b4ff',
			light: '#fbbfff',
			dark: '#e879f9',
			gradient: 'linear-gradient(135deg, #f5b4ff 0%, #fb7185 100%)',
		},
		accent: {
			teal: '#2dd4bf',
			purple: '#c084fc',
			pink: '#f9a8d4',
			orange: '#fdba74',
		},
		// Neutral colors (inverted for dark mode)
		neutral: {
			50: '#000000',
			100: '#1c1c1e',
			200: '#38383a',
			300: '#48484a',
			400: '#636366',
			500: '#8e8e93',
			600: '#aeaeb2',
			700: '#d1d1d6',
			800: '#e5e5ea',
			900: '#f5f5f7',
		},
		// Semantic colors (adjusted for dark mode)
		text: {
			primary: '#f5f5f7',
			secondary: '#d1d1d6',
			tertiary: '#8e8e93',
			inverse: '#1d1d1f',
		},
		background: {
			primary: '#000000',
			secondary: '#1c1c1e',
			tertiary: '#2c2c2e',
		},
	},

	typography: lightTheme.typography,
	spacing: lightTheme.spacing,
	borderRadius: lightTheme.borderRadius,

	shadows: {
		sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
		base: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
		md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
		lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
		xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
		'2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
		inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)',
		glow: '0 0 20px rgba(129, 140, 248, 0.5)',
		glowHover: '0 0 30px rgba(129, 140, 248, 0.7)',
	},

	transitions: lightTheme.transitions,
	breakpoints: lightTheme.breakpoints,

	glassmorphism: {
		light: `
			background: rgba(255, 255, 255, 0.1);
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
			border: 1px solid rgba(255, 255, 255, 0.2);
		`,
		dark: `
			background: rgba(0, 0, 0, 0.4);
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
			border: 1px solid rgba(255, 255, 255, 0.1);
		`,
	},

	animations: lightTheme.animations,
};

// デフォルトテーマはダークモード
export const theme = darkTheme;

export default theme;
